import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Briefcase, GraduationCap, Target, Users, Award, CheckCircle, LineChart, Shield, Lightbulb, HandshakeIcon } from 'lucide-react';

const Services = () => {
  const mainServices = [
    {
      icon: TrendingUp,
      title: 'Financial Planning',
      description: 'Comprehensive financial strategies tailored to your unique goals and circumstances.',
      features: [
        'Personalized financial assessments',
        'Investment portfolio management',
        'Retirement planning strategies',
        'Tax optimization guidance',
        'Estate planning assistance',
        'Emergency fund development'
      ],
      image: 'https://images.pexels.com/photos/8276364/pexels-photo-8276364.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      icon: Briefcase,
      title: 'Wealth Building',
      description: 'Proven methodologies to grow, protect, and multiply your wealth across generations.',
      features: [
        'Diversified investment strategies',
        'Real estate investment guidance',
        'Business ownership pathways',
        'Passive income development',
        'Asset protection planning',
        'Generational wealth transfer'
      ],
      image: 'https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoYW5kc2hha2UlMjBidXNpbmVzcyUyMGRlYWx8ZW58MHx8fHwxNzcxNjUyMzA4fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: GraduationCap,
      title: 'Leadership Development',
      description: 'Transform into an influential leader with our comprehensive development programs.',
      features: [
        'Executive coaching sessions',
        'Communication mastery training',
        'Strategic thinking workshops',
        'Team building expertise',
        'Conflict resolution skills',
        'Public speaking development'
      ],
      image: 'https://images.pexels.com/photos/5945809/pexels-photo-5945809.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      icon: Target,
      title: 'Mentorship Programs',
      description: 'One-on-one guidance from industry experts to accelerate your journey to success.',
      features: [
        'Personalized mentorship matching',
        'Goal setting and accountability',
        'Network building opportunities',
        'Industry insights access',
        'Career advancement strategies',
        'Personal brand development'
      ],
      image: 'https://images.unsplash.com/photo-1768448808550-3148cce53a19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHNlbWluYXIlMjBhdWRpZW5jZSUyMGV2ZW50fGVufDB8fHx8MTc3MTY1MjMwN3ww&ixlib=rb-4.1.0&q=85'
    }
  ];

  const additionalServices = [
    {
      icon: LineChart,
      title: 'Market Analysis',
      description: 'In-depth market research and analysis to inform your investment decisions.'
    },
    {
      icon: Shield,
      title: 'Risk Management',
      description: 'Comprehensive risk assessment and mitigation strategies for your portfolio.'
    },
    {
      icon: Lightbulb,
      title: 'Business Consulting',
      description: 'Expert guidance for entrepreneurs and business owners seeking growth.'
    },
    {
      icon: HandshakeIcon,
      title: 'Partnership Opportunities',
      description: 'Strategic partnership development for business expansion and networking.'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Discovery Call',
      description: 'We start with a comprehensive consultation to understand your goals, current situation, and aspirations.'
    },
    {
      step: '02',
      title: 'Custom Strategy',
      description: 'Our experts develop a personalized roadmap tailored to your unique circumstances and objectives.'
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'We guide you through executing your strategy with ongoing support and accountability.'
    },
    {
      step: '04',
      title: 'Growth & Review',
      description: 'Regular check-ins and adjustments ensure you stay on track toward your financial goals.'
    }
  ];

  return (
    <div data-testid="services-page" className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-6">Our Services</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-8">
              Comprehensive Solutions for <span className="text-gold-gradient">Wealth</span> & Leadership
            </h1>
            <p className="font-body text-white/70 text-base sm:text-lg leading-relaxed">
              From financial planning to leadership development, we offer a complete suite of services designed to transform your future and unlock your full potential.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24 lg:space-y-32">
            {mainServices.map((service, index) => (
              <div
                key={index}
                data-testid={`main-service-${index}`}
                className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="w-16 h-16 bg-gold-500/10 flex items-center justify-center mb-8">
                    <service.icon className="w-8 h-8 text-gold-500" />
                  </div>
                  <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">{service.title}</h2>
                  <p className="font-body text-white/60 text-base sm:text-lg leading-relaxed mb-8">
                    {service.description}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-gold-500 mt-0.5 flex-shrink-0" />
                        <span className="font-body text-white/70 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/careers"
                    className="inline-flex items-center gap-2 font-body text-sm text-gold-500 uppercase tracking-widest hover:gap-3 transition-all duration-300"
                  >
                    Get Started
                    <ArrowRight size={14} />
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-[400px] lg:h-[500px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gold-500/5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-4">More Solutions</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Additional <span className="text-gold-gradient">Services</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalServices.map((service, index) => (
              <div
                key={index}
                data-testid={`additional-service-${index}`}
                className="group p-8 bg-white/[0.02] border border-white/5 hover:border-gold-500/30 transition-all duration-500"
              >
                <service.icon className="w-10 h-10 text-gold-500 mb-6 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="font-heading text-xl text-white mb-4">{service.title}</h3>
                <p className="font-body text-white/50 text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-4">How It Works</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Our <span className="text-gold-gradient">Process</span>
            </h2>
            <p className="font-body text-white/60 max-w-2xl mx-auto">
              A proven methodology designed to guide you from where you are to where you want to be.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div
                key={index}
                data-testid={`process-step-${index}`}
                className="relative"
              >
                <div className="text-gold-500/20 font-heading text-8xl mb-4">{step.step}</div>
                <h3 className="font-heading text-xl text-white mb-4">{step.title}</h3>
                <p className="font-body text-white/50 text-sm leading-relaxed">{step.description}</p>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-px bg-white/10 -translate-x-1/2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-4">Why Us</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                Why Choose <span className="text-gold-gradient">Empower Wealth</span>?
              </h2>
              <p className="font-body text-white/60 text-base sm:text-lg leading-relaxed mb-8">
                With years of experience and a track record of success, we've helped hundreds of families transform their financial futures.
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-gold-500" />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg text-white mb-2">Proven Track Record</h4>
                    <p className="font-body text-white/50 text-sm">400+ families empowered with 99% satisfaction rate.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-gold-500" />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg text-white mb-2">Expert Team</h4>
                    <p className="font-body text-white/50 text-sm">Seasoned professionals with decades of combined experience.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-gold-500" />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg text-white mb-2">Personalized Approach</h4>
                    <p className="font-body text-white/50 text-sm">Tailored strategies that fit your unique goals and circumstances.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-8 bg-white/[0.02] border border-white/5 text-center">
                <div className="font-heading text-4xl text-gold-500 mb-2">400+</div>
                <div className="font-body text-white/50 text-sm uppercase tracking-wider">Families Served</div>
              </div>
              <div className="p-8 bg-white/[0.02] border border-white/5 text-center">
                <div className="font-heading text-4xl text-gold-500 mb-2">99%</div>
                <div className="font-body text-white/50 text-sm uppercase tracking-wider">Satisfaction</div>
              </div>
              <div className="p-8 bg-white/[0.02] border border-white/5 text-center">
                <div className="font-heading text-4xl text-gold-500 mb-2">4+</div>
                <div className="font-body text-white/50 text-sm uppercase tracking-wider">Years Experience</div>
              </div>
              <div className="p-8 bg-white/[0.02] border border-white/5 text-center">
                <div className="font-heading text-4xl text-gold-500 mb-2">100+</div>
                <div className="font-body text-white/50 text-sm uppercase tracking-wider">Events Hosted</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-[#050505] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Ready to <span className="text-gold-gradient">Get Started</span>?
            </h2>
            <p className="font-body text-white/60 text-base sm:text-lg mb-12">
              Take the first step toward financial freedom and leadership excellence. Schedule your free consultation today.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/careers"
                data-testid="services-cta-join"
                className="inline-flex items-center justify-center gap-2 bg-gold-500 text-black px-10 py-4 font-body font-semibold uppercase tracking-widest hover:bg-gold-400 transition-colors duration-300"
              >
                Join Our Team
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/about"
                data-testid="services-cta-contact"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-10 py-4 font-body font-semibold uppercase tracking-widest hover:border-gold-500 hover:text-gold-500 transition-colors duration-300"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
